import { RawParams, TransitionService, UIRouterGlobals } from "@uirouter/angular";
import { LEFT_ARROW, RIGHT_ARROW } from "@angular/cdk/keycodes";
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Observable, Subscription, map, of, take, takeUntil, zip } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { HomeScreenView } from "@gtmhub/home/utils";
import { InsightboardService } from "@gtmhub/insightboards";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { NavigationEventType } from "@gtmhub/navigation";
import { NotificationsInboxService } from "@gtmhub/notifications/controllers/notifications-inbox";
import { BASE_OKR_VIEWS_STATE, BASE_OKR_VIEW_STATE } from "@gtmhub/okrs/okrs.states";
import { IReport } from "@gtmhub/reporting/models";
import { filterActiveSessions } from "@gtmhub/sessions/redux/session-selectors";
import { setTopNavBarProps } from "@gtmhub/shared/components/ui/top-navigation-bar/utils";
import { INotificationsCountData } from "@gtmhub/sockets";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { byDateField } from "@gtmhub/util/sorting-utils";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ICollection } from "@webapp/core/core.models";
import { fromTransitionHook } from "@webapp/core/routing/rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { FeatureModuleService } from "@webapp/feature-toggles/services/feature-module.service";
import { NewNavigationEvents } from "@webapp/navigation/components/navigation/events";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { IScreen, Screens } from "@webapp/navigation/models/shared.models";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { NavigationReportsCacheService } from "@webapp/navigation/services/navigation-reports-cache.service";
import { QuantiveStaticBlockCheckerService } from "@webapp/navigation/services/quantive-static-block-checker.service";
import { SettingsSubNavStateService } from "@webapp/navigation/services/settings/settings-sub-nav-state.service";
import { SettingsVisibilityService } from "@webapp/navigation/services/settings/settings-visibility.service";
import { TodoActionsService } from "@webapp/navigation/services/todo-actions.service";
import NavigationItemsMediator from "@webapp/navigation/services/uxcustomization/navigation-items.mediator.service";
import { NotificationsFacade } from "@webapp/notifications/services/notifications/notifications-facade.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { Session } from "@webapp/sessions/models/sessions.model";
import { SessionsRepository } from "@webapp/sessions/services/sessions-repository.service";
import { ProfileThemeService } from "@webapp/user-profile/services/profile-theme.service";
import { setNavWidth } from "../../utils/navigation.util";
import { localize } from "./../../../localization/utils/localization.utils";
import { NAV_WIDTH, TOP_NAV_BAR_PROPS } from "./config";

type ScreenModifier = (screen: IScreen) => IScreen;

@Component({
  selector: "navigation-v2",
  templateUrl: "./navigation.component.html",
  styleUrls: ["./navigation.component.less"],
  encapsulation: ViewEncapsulation.None,
})
export class NavigationComponent implements OnInit, OnDestroy {
  private activeHomeScreen: "gtmhub.home.feed" | "gtmhub.home.dashboard" = "gtmhub.home.dashboard";
  private switchToDashboardSubscription: Subscription;
  private switchToFeedSubscription: Subscription;
  private unreadNotificationsCountSubscription: Subscription;
  public screens: IScreen[] = [];
  public screensBase: IScreen[] = [
    {
      icon: "home",
      state: "gtmhub.home.dashboard",
      key: "home",
      ariaLabel: "home",
      showScreenCondition$: of(true),
    },
    {
      icon: "target",
      state: "gtmhub.goals.all",
      key: "okrs",
      subStates: ["gtmhub.goals", "gtmhub.sessions", BASE_OKR_VIEWS_STATE, BASE_OKR_VIEW_STATE, "gtmhub.workflow"],
      ariaLabel: "okr_aria_label",
      showScreenCondition$: this.permissionsFacade.hasPermission$("AccessGoals").pipe(map((v) => v && this.accountService.featureAvailable("okrs"))),
    },
    {
      icon: "gh-kpi",
      state: "gtmhub.kpis",
      key: "kpis",
      subStates: ["gtmhub.kpis"],
      ariaLabel: "kpi_aria_label",
      showScreenCondition$: this.featureModuleService.isKpiModuleEnabled$(),
    },
    {
      icon: "task",
      state: "gtmhub.tasks",
      key: "tasks",
      subStates: ["gtmhub.tasks"],
      ariaLabel: "tasks_capital",
      showScreenCondition$: this.featureModuleService.isTaskModuleEnabled$(),
    },
    {
      icon: "gh-whiteboards",
      state: "gtmhub.whiteboards",
      key: "whiteboards",
      subStates: ["gtmhub.whiteboard"],
      ariaLabel: "whiteboards",
      showScreenCondition$: this.featureModuleService.isWhiteboardModuleEnabled$(),
    },
    {
      icon: "pie",
      state: "gtmhub.lists",
      key: "reports",
      subStates: ["gtmhub.lists", "gtmhub.list", "gtmhub.insightboard"],
      ariaLabel: "reports",
      showScreenCondition$: of(true),
    },
    {
      icon: "gh-insightboards",
      state: "gtmhub.insightboards",
      key: "insightboards",
      subStates: ["gtmhub.insightboard", "gtmhub.insightboards"],
      ariaLabel: "insightboards",
      showScreenCondition$: this.permissionsFacade.hasPermission$("AccessInsightboards").pipe(map((v) => v && this.accountService.featureAvailable("insightboards"))),
    },
    {
      icon: "users-group",
      state: "gtmhub.employees",
      key: "people",
      subStates: ["gtmhub.teams", "gtmhub.employees", "gtmhub.organization", "gtmhub.organization_chart"],
      ariaLabel: "employees",
      showScreenCondition$: this.permissionsFacade.hasPermission$("AccessPeople"),
    },
  ];
  public settingsScreen: IScreen = {
    icon: "manage",
    state: "gtmhub.configuration",
    key: "settings",
    subStates: [
      "gtmhub.configuration",
      "gtmhub.automation",
      "gtmhub.automationList",
      "gtmhub.usersList",
      "gtmhub.badges",
      "gtmhub.approveBadges",
      "gtmhub.roles",
      "gtmhub.dataflow",
    ],
    showScreenCondition$: this.settingsVisibility.showSettings$,
  };
  public activeScreen: Screens | null;
  public unreadNotificationsCountAriaLabel: string;
  public showHomeActionsBadge$: Observable<boolean>;
  public showMarketplace$: Observable<boolean>;
  public isNavExpanded: boolean;
  public theme: string;
  public unreadNotificationsCount: number;
  private paramsMap: { [key in Screens]?: RawParams } = {};
  private specialReportIds: string[] = [];
  private screenModifiers: ScreenModifier[] = [];

  constructor(
    private routerGlobals: UIRouterGlobals,
    private transitionService: TransitionService,
    private navigationReportsCache: NavigationReportsCacheService,
    private insightboardService: InsightboardService,
    private accountService: AccountService,
    private currentUserService: CurrentUserService,
    private featureModuleService: FeatureModuleService,
    private notificationsFacade: NotificationsFacade,
    private notificationsInboxService: NotificationsInboxService,
    private permissionsFacade: PermissionsFacade,
    private broadcastService: BroadcastService,
    private settingsVisibility: SettingsVisibilityService,
    private settingsSubNavStateService: SettingsSubNavStateService,
    private isNavigationExpandedCache: IsNavigationExpandedCache,
    private navigationItemsMediator: NavigationItemsMediator,
    private sessionsRepository: SessionsRepository,
    private todoActionsService: TodoActionsService,
    private profileThemeService: ProfileThemeService,
    private destroyService$: DestroyService,
    private analyticsService: AnalyticsService,
    private blobChecker: QuantiveStaticBlockCheckerService,
    @Inject("$rootScope") private rootScope: IGtmhubRootScopeService
  ) {
    isNavigationExpandedCache.get$().subscribe((value) => {
      this.isNavExpanded = value;
      this.rootScope.hideMenuV2 = !value;
      this.rootScope.$evalAsync();
    });

    this.setActiveHomeScreen();
    this.switchToDashboardSubscription = this.broadcastService.on(NewNavigationEvents.SUB_NAV_SWITCH_TO_DASHBOARD).subscribe(() => {
      this.setActiveHomeScreen(HomeScreenView.dashboard);
      this.updateScreens();
    });

    this.switchToFeedSubscription = this.broadcastService.on(NewNavigationEvents.SUB_NAV_SWITCH_TO_FEED).subscribe(() => {
      this.setActiveHomeScreen(HomeScreenView.feed);
      this.updateScreens();
    });

    setNavWidth({ expanded: NAV_WIDTH.expanded + "px", collapsed: NAV_WIDTH.collapsed + "px" });
    setTopNavBarProps({ height: TOP_NAV_BAR_PROPS.height + "px", borderColor: TOP_NAV_BAR_PROPS.borderColor });

    this.theme = this.profileThemeService.getProfileThemeCurrentValue().toLowerCase();
    this.profileThemeService
      .getProfileTheme$()
      .pipe(takeUntil(this.destroyService$))
      .subscribe((value) => {
        this.theme = value.toLowerCase();
      });

    // TODO: [Zdravko Donev] Remove this function when we stop seeing customers get quantivestatic_blocked_by_customer error
    this.blobChecker.checkIfQuantivestaticIsBlocked();
  }

  public ngOnInit(): void {
    fromTransitionHook(this.transitionService, "onSuccess", {})
      .pipe(takeUntil(this.destroyService$))
      .subscribe(() => {
        this.setActiveScreen();
      });

    this.paramsMap["tasks"] = { filterId: null };

    this.processRecentSessions();
    window.setTimeoutOutsideAngular(() => this.broadcastService.emit(NavigationEventType.NAVIGATION_LOADED));

    // setting up reports permissions
    zip(this.permissionsFacade.hasPermission$("ManageData"), this.permissionsFacade.hasPermission$("AccessReportsAndDataSourceFilters"))
      .pipe(
        map(([canManageData, canAccessReportsAndDataSourceFilters]) => {
          return { hasAccessToSpecialReports: canManageData || canAccessReportsAndDataSourceFilters };
        })
      )
      .subscribe(({ hasAccessToSpecialReports }: { hasAccessToSpecialReports: boolean }) => {
        if (hasAccessToSpecialReports) {
          this.insightboardService.getReports().then((reports: IReport[]) => {
            this.navigationReportsCache.set(reports);
            const performanceReport = reports.find((report) => report.name.toLowerCase() === "performance report");
            this.paramsMap["reports"] = { dashboardId: performanceReport.id };
            this.updateScreens((screen: IScreen) => {
              if (screen.key !== "reports") {
                return screen;
              }
              return {
                ...screen,
                state: "gtmhub.insightboard",
              };
            });
            this.specialReportIds = reports.map((r) => r.id);
            this.setActiveScreen();
          });
        } else {
          this.setActiveScreen();
        }
      });

    // settings visibility
    this.settingsSubNavStateService
      .firstVisibleState$()
      .pipe(take(1))
      .subscribe((v) => (this.settingsScreen.state = v));

    this.showMarketplace$ = this.permissionsFacade.hasPermission$("ManageGoals").pipe(map((v) => v && this.accountService.featureAvailable("marketplace")));

    this.unreadNotificationsCountSubscription = this.notificationsFacade.getUnreadNotificationsCount$().subscribe((count) => {
      this.setAriaLabel(count);
      this.unreadNotificationsCount = count;
    });
    this.showHomeActionsBadge$ = this.todoActionsService.actionsCount$().pipe(map((count) => count > 0));
    this.subscribeToNotificationsCountUpdates();
    this.updateSurveyStorageParams();
  }

  public ngOnDestroy(): void {
    if (this.switchToDashboardSubscription) {
      this.switchToDashboardSubscription.unsubscribe();
      this.switchToDashboardSubscription = null;
    }
    if (this.switchToFeedSubscription) {
      this.switchToFeedSubscription.unsubscribe();
      this.switchToFeedSubscription = null;
    }
    if (this.unreadNotificationsCountSubscription) {
      this.unreadNotificationsCountSubscription.unsubscribe();
      this.unreadNotificationsCountSubscription = null;
    }
  }

  public openNotificationsModal(): void {
    this.notificationsInboxService.open({}, this.unreadNotificationsCount);
    this.analyticsService.track("Notification Modal Opened", { user_id: getCurrentUserId() });
  }

  public setMarketplaceCookie(): void {
    this.currentUserService.setMarketplaceRedirectCookie();
  }
  private setAriaLabel(newNotifications?: number): void {
    this.unreadNotificationsCountAriaLabel = newNotifications
      ? localize("notifications") + " " + localize("new_notifications", { newNotifications })
      : localize("notifications");
  }

  public onKeyDown(event: KeyboardEvent): void {
    switch (event.keyCode || event.code) {
      case RIGHT_ARROW:
        {
          event.preventDefault();
          const subNavElement = document.querySelector("#sub-navigation toggle-nav button") as HTMLElement;
          if (subNavElement) {
            setTimeout(() => {
              subNavElement.focus();
            }, 100);
          }
        }
        break;
      case LEFT_ARROW:
        {
          event.preventDefault();
          const mainNavElement = document.querySelector("#main-navigation a") as HTMLElement;
          if (mainNavElement) {
            setTimeout(() => {
              mainNavElement.focus();
            }, 100);
          }
        }
        break;
    }
  }

  private updateScreens(screenModificator?: (screen: IScreen) => IScreen): void {
    if (screenModificator) {
      this.screenModifiers.push(screenModificator);
    }

    const newScreens = this.getScreens();

    this.screens = newScreens;
  }

  private processRecentSessions(): void {
    // if the user has visited the app before, take him to his last session
    // if not, take him either to the only active session or to the one that he can create OKRs in and ends sooner than the others
    this.navigationItemsMediator.loadRecents("sessions");
    this.navigationItemsMediator
      .getRecentItems$("sessions")
      .pipe(takeUntil(this.destroyService$))
      .subscribe((recentSessions: ICollection<INavItem>) => {
        if (!recentSessions) return;
        const sessions = recentSessions.items.filter((session) => session.uxcType === "session");
        if (sessions.length) {
          this.paramsMap["okrs"] = { planningSessionId: sessions[0].id };
          this.updateScreens();
        } else {
          this.setSessionMenuItemForNewUsers();
        }
      });
  }

  private setSessionMenuItemForNewUsers(): void {
    this.sessionsRepository
      .getMap$()
      .pipe(takeUntil(this.destroyService$))
      .subscribe((map) => {
        this.fillInSessionMenuItem(Array.from(map.values()));
      });
  }

  private fillInSessionMenuItem(sessions: Session[]): void {
    const activeSessions = filterActiveSessions(sessions);
    let chosenSession: Session | undefined = undefined;
    if (activeSessions.length === 0) {
      this.paramsMap["okrs"] = null;
      this.updateScreens((screen: IScreen) => {
        if (screen.key !== "okrs") {
          return screen;
        }
        return {
          ...screen,
          state: "gtmhub.sessions",
        };
      });
      return;
    }
    if (activeSessions.length === 1) {
      chosenSession = activeSessions[0];
    } else {
      const chronoSortedSessions = activeSessions.sort(byDateField("end"));
      const sessionsUserCanCreateIn = chronoSortedSessions.filter((session) => this.currentUserService.allowedActionsSync(session.access).includes("create"));
      if (sessionsUserCanCreateIn.length) {
        chosenSession = sessionsUserCanCreateIn[0];
      } else {
        chosenSession = chronoSortedSessions[0];
      }
    }
    this.paramsMap["okrs"] = { planningSessionId: chosenSession.id };
    this.updateScreens();
  }

  private setActiveScreen(): void {
    const currentState = this.routerGlobals.current.name;
    const collapsedNavStates = [
      "gtmhub.userProfile",
      "gtmhub.profileTheme",
      "gtmhub.userLanguage",
      "gtmhub.sendChangePasswordRequest",
      "gtmhub.userNotificationSettings",
    ];

    if (currentState === "gtmhub.insightboard" || currentState.startsWith("gtmhub.insightboard.")) {
      const idParam = this.routerGlobals.params["dashboardId"];
      this.activeScreen = this.specialReportIds.includes(idParam) ? "reports" : "insightboards";
      return;
    }

    const allScreens = [...this.screens, this.settingsScreen];
    const currentScreen = allScreens.find(
      (s) => currentState === s.state || s.subStates?.some((subState) => currentState === subState || currentState.startsWith(`${subState}.`))
    );

    this.activeScreen = currentScreen?.key || "home";
    if (collapsedNavStates.includes(currentState)) {
      this.isNavigationExpandedCache.set({ value: false, persistValue: false });
    } else {
      this.isNavigationExpandedCache.resetTempValue();
    }
  }

  private getScreens(): IScreen[] {
    this.ensureHomePointsToCorrectHomeScreen();
    const screens: IScreen[] = this.screensBase;

    const modifiedScreens = this.screenModifiers.reduce((prev: IScreen[], current: ScreenModifier) => {
      return prev.map(current);
    }, screens);
    modifiedScreens.forEach((screen) => {
      screen.stateParams = this.paramsMap[screen.key] || null;
    });

    return modifiedScreens;
  }

  private ensureHomePointsToCorrectHomeScreen(): void {
    const homeState = this.screensBase.find((screen) => screen.key === "home");
    homeState.state = this.activeHomeScreen;
  }

  private subscribeToNotificationsCountUpdates(): void {
    const updateCount = (count: number): void => this.notificationsFacade.updateUnreadNotificationsCount(count);
    this.broadcastService.on<number>("newMessagesCount").pipe(takeUntil(this.destroyService$)).subscribe(updateCount);
    this.broadcastService
      .on<INotificationsCountData>("notificationsCount")
      .pipe(
        map((collection) => collection.totalCount),
        takeUntil(this.destroyService$)
      )
      .subscribe(updateCount);
  }

  private updateSurveyStorageParams(): void {
    const previousSeenNewNaviCount = Number(localStorage.getItem("seenNewNaviCount"));
    const seenNewNaviCount = previousSeenNewNaviCount ? previousSeenNewNaviCount + 1 : 1;
    localStorage.setItem("seenNewNaviCount", JSON.stringify(seenNewNaviCount));
  }

  private setActiveHomeScreen(screen?: HomeScreenView): void {
    if (!screen) {
      screen = this.currentUserService.getUserSetting("homeScreen");
    }
    if (screen === HomeScreenView.feed) {
      this.activeHomeScreen = "gtmhub.home.feed";
    } else {
      this.activeHomeScreen = "gtmhub.home.dashboard";
    }
  }
}
